import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Divider, Button, Flex, Typography, Space } from 'antd';
import { OrganizationMemberManager } from '@copilot/data';
import { OrganizationMemberActions } from '@copilot/common/store/actions/organizationMember';
import notificationManager from '@copilot/common/utils/notificationManager';
import EditableTextDisplay from '@copilot/common/components/forms/common/inputs/editableTextDisplay';
import { IOrganizationMember } from '@copilot/common/store/models/redux';
import { ERROR_ICON, OK_ICON, SYNCING_ICON } from '@copilot/common/components/icon';
import styles from './userSettings.module.less';
import { disconnectMyLinkedIn } from './serviceCalls';
import { LinkedInMetaActions } from '@copilot/common/store/actions/linkedInMeta';
import { USER_SETTINGS_LINKEDIN_CONNECT_TRACKING_ID } from '@copilot/common/tracking/userpilotEventConsts';
import { useNavigateToHostedAuthUrl } from '@copilot/common/hooks/linkedin/hostedAuth/useNavigateToHostedAuthUrl';
import { HAS_DISMISSED_ACCOUNT_ERROR_STATUS_STORAGE_KEY } from '../../layouts/headerSider/header/constant';
import { useAIDataUsageTermsOfUse } from '@copilot/common/hooks/termsOfUse';
import { CheckCircleFilled, SafetyOutlined } from '@ant-design/icons';
import AIDataUsageTermsOfUseModal from '@copilot/common/components/modals/wrappers/aiDataUsageTermsOfUse';

type UserSettingsProps = {
    orgMember: IOrganizationMember;
    hideLinkedIn?: boolean;
};

/**
 * Settings config for the user
 * @param param0
 * @returns
 */
export default function UserSettings({ orgMember, hideLinkedIn }: UserSettingsProps) {
    const storeDispatch = useDispatch();

    const navigateToHostedAuthUrl = useNavigateToHostedAuthUrl(orgMember.id, window.location.href);

    const {
        hasAcknowledged: hasAcknowledgedAIDataUsageTerms,
        acknowledgeTermsOfUse,
        isLoading,
    } = useAIDataUsageTermsOfUse();

    const [isRefreshingSalesNav, setIsRefreshingSalesNav] = useState(false);
    const [isReconnecting, setIsReconnecting] = useState(false);
    const [isDisconnectingLinkedIn, setIsDisconnectingLinkedIn] = useState(false);
    const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

    function onUpdatePhoneNumber(phoneNumber: string) {
        OrganizationMemberManager.updateSelf({ phoneNumber }).then((member) => {
            storeDispatch(OrganizationMemberActions.loadOrganizationMember(member));
        });
    }

    async function onRefreshSalesNav() {
        try {
            setIsRefreshingSalesNav(true);
            await navigateToHostedAuthUrl();
        } catch (e) {
            notificationManager.showErrorNotification({
                message: 'Error',
                description:
                    'There was an issue syncing sales navigator, please refresh and try again.',
            });
        } finally {
            // Add a brief delay to prevent the user from spamming the sync button after an error
            // Not relevant to the success case since the user is redirected to a new page
            await new Promise((resolve) => setTimeout(resolve, 1000));
            setIsRefreshingSalesNav(false);
        }
    }

    async function onDisconnectLinkedIn() {
        try {
            setIsDisconnectingLinkedIn(true);
            const updatedMember = await disconnectMyLinkedIn();
            const isLoggedIn = await OrganizationMemberManager.checkIsLoggedIn(updatedMember.id);
            if (isLoggedIn === false) {
                if (orgMember.isLiSessionInRetryState) {
                    storeDispatch(OrganizationMemberActions.loadOrganizationMember(updatedMember));
                }
                storeDispatch(
                    LinkedInMetaActions.loadLinkedInMeta({
                        orgMemberId: updatedMember.id,
                        isLoggedIn,
                    })
                );
                notificationManager.showSuccessNotification({
                    message: 'Success',
                    description: 'LinkedIn was successfully disconnected',
                });
            } else {
                throw new Error('Failed to disconnect LinkedIn');
            }
        } catch (e) {
            notificationManager.showErrorNotification({
                message: 'Error',
                description:
                    'There was an issue disconnecting LinkedIn, please refresh and try again.',
            });
        }
        setIsDisconnectingLinkedIn(false);
    }

    async function onReconnect() {
        // Check if AI Data Usage Terms are acknowledged first
        if (!hasAcknowledgedAIDataUsageTerms) {
            setIsTermsModalOpen(true);
            return;
        }

        // If terms are already acknowledged, proceed directly
        await proceedWithLinkedInReconnection();
    }

    async function proceedWithLinkedInReconnection() {
        try {
            setIsReconnecting(true);
            localStorage.removeItem(HAS_DISMISSED_ACCOUNT_ERROR_STATUS_STORAGE_KEY);
            const isLoggedIn = await OrganizationMemberManager.checkIsLoggedIn(orgMember.id);
            if (isLoggedIn) {
                notificationManager.showInfoNotification({
                    message: 'Already Connected',
                    description: 'Your account is already connected to LinkedIn.',
                });
                storeDispatch(
                    LinkedInMetaActions.loadLinkedInMeta({
                        orgMemberId: orgMember.id,
                        isLoggedIn,
                    })
                );
                return;
            }
            await navigateToHostedAuthUrl();
        } catch (e) {
            console.error("Couldn't generate LinkedIn auth URL", e);
            notificationManager.showErrorNotification({
                message:
                    'Oops! Something went wrong. Please try again in a moment, or contact support if this problem persists.',
                description: "Couldn't generate LinkedIn auth URL",
            });
        } finally {
            // Add a brief delay to prevent the user from spamming the reconnect button after an error
            // Not relevant to the success case since the user is redirected to a new page
            await new Promise((resolve) => setTimeout(resolve, 1000));
            setIsReconnecting(false);
        }
    }

    return (
        <div className={styles.userSettingsWrapper}>
            <Flex gap="small">
                <label>First Name</label>
                <div>{orgMember.firstName}</div>
            </Flex>
            <Flex gap="small">
                <label>Last Name</label>
                <div>{orgMember.lastName}</div>
            </Flex>
            <Flex gap="small">
                <label>Email</label>
                <div>{orgMember.email}</div>
            </Flex>
            <Flex gap="small">
                <label>Phone number</label>
                <EditableTextDisplay
                    value={orgMember.phoneNumber}
                    confirmHandler={onUpdatePhoneNumber}
                    confirmOnBlur
                >
                    {orgMember.phoneNumber}
                </EditableTextDisplay>
            </Flex>
            {!hideLinkedIn && (
                <>
                    <Flex gap="small">
                        <label>LinkedIn session status</label>
                        {orgMember.isLinkedInLoggedIn || orgMember.isLiSessionInRetryState ? (
                            <div>
                                <Flex gap="small" align="center">
                                    {orgMember.isLinkedInLoggedIn ? OK_ICON : SYNCING_ICON}
                                    {orgMember.isLinkedInLoggedIn ? 'Connected' : 'Syncing'}
                                    <Button
                                        size="small"
                                        onClick={() => void onDisconnectLinkedIn()}
                                        loading={isDisconnectingLinkedIn}
                                    >
                                        Disconnect
                                    </Button>
                                </Flex>
                                <div className={styles.statusDescription}>
                                    {orgMember.isLinkedInLoggedIn ? (
                                        <>
                                            Your LinkedIn is currently connected to our platform,
                                            automation will run if you have an active LinkedIn Sales
                                            Navigator subscription.
                                        </>
                                    ) : (
                                        <>
                                            We are attempting to sync your LinkedIn account.
                                            Outgoing messages will be paused and uploading search
                                            lists will be temporarily unavailable until the
                                            connection is established.
                                        </>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div>
                                <Flex gap="small" align="center">
                                    {ERROR_ICON}
                                    Disconnected
                                    <Button
                                        size="small"
                                        onClick={() => void onReconnect()}
                                        loading={isRefreshingSalesNav || isReconnecting}
                                        data-tracking-id={
                                            USER_SETTINGS_LINKEDIN_CONNECT_TRACKING_ID
                                        }
                                    >
                                        Reconnect
                                    </Button>
                                </Flex>
                                <div className={styles.statusDescription}>
                                    Your LinkedIn is currently not connected to our platform,
                                    preventing automation. Click "Reconnect" to connect your
                                    LinkedIn account.
                                </div>
                            </div>
                        )}
                    </Flex>
                    {orgMember.isLinkedInLoggedIn && (
                        <Flex gap="small">
                            <label>Sales Navigator status</label>
                            {orgMember.hasInvalidSalesNavSubscription ? (
                                <div>
                                    <Flex gap="small" align="center">
                                        {ERROR_ICON}
                                        Paused
                                        <Button
                                            size="small"
                                            onClick={() => void onRefreshSalesNav()}
                                            loading={isRefreshingSalesNav}
                                        >
                                            Sync Sales Navigator
                                        </Button>
                                    </Flex>
                                    <div className={styles.statusDescription}>
                                        Automation has paused because we can't detect a valid
                                        LinkedIn Sales Navigator subscription for your account.
                                        Please ensure you have an active LinkedIn Sales Navigator
                                        subscription and then click "Sync Sales Navigator"
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <Flex gap="small" align="center">
                                        {OK_ICON} Active
                                    </Flex>
                                    <div className={styles.statusDescription}>
                                        Your LinkedIn Sales Navigator subscription is marked as
                                        active and automation is running
                                    </div>
                                </div>
                            )}
                        </Flex>
                    )}
                </>
            )}
            {hasAcknowledgedAIDataUsageTerms && (
                <>
                    <Divider />
                    <div className={styles.privacyNoticeSection}>
                        <Flex gap="small" align="center" className={styles.privacyNoticeHeader}>
                            <div className={styles.safetyIconContainer}>
                                <SafetyOutlined className={styles.safetyIcon} />
                            </div>
                            <Typography.Title level={5} className={styles.dataTitle}>
                                Your Data
                            </Typography.Title>
                        </Flex>

                        <Divider />

                        <Space direction="vertical" size={16} className={styles.fullWidth}>
                            <Typography.Paragraph>
                                We prioritize your data security and privacy. Your data is handled
                                in accordance with our privacy policy and OpenAI's data usage
                                policies. We don't sell your data. To improve functionality and
                                provide personalized recommendations, we may process certain data,
                                such as your LinkedIn profile and interactions within the app. Some
                                features use AI models from OpenAI and other trusted providers.
                            </Typography.Paragraph>

                            <div className={styles.protectedNotice}>
                                <Flex gap="small" align="flex-start">
                                    <CheckCircleFilled className={styles.protectedNoticeIcon} />
                                    <div>
                                        <Typography.Text strong>
                                            Your data is protected
                                        </Typography.Text>
                                        <Typography.Paragraph
                                            className={styles.protectedNoticeText}
                                        >
                                            It is processed securely and never sold or shared
                                            outside of these purposes.
                                        </Typography.Paragraph>
                                    </div>
                                </Flex>
                            </div>
                        </Space>
                    </div>
                </>
            )}
            <AIDataUsageTermsOfUseModal
                open={isTermsModalOpen}
                onOpenChange={setIsTermsModalOpen}
                acknowledgeTermsOfUse={acknowledgeTermsOfUse}
                isAcknowledgementLoading={isLoading}
                onAcknowledge={() => {
                    void proceedWithLinkedInReconnection();
                }}
            />
        </div>
    );
}
